/**
 * Helpers for the Kai `/` slash-command tokenizer.
 *
 * A slash command is a single token at the very start of the message. The menu
 * stays open while the caret is inside that leading `/command` token (before any
 * whitespace); once the user types a space the token is complete and the menu
 * closes (the command is only committed by selecting it, which inserts a pill).
 */

import type { KaiSlashCommand } from "@/services/kaiApi";

/**
 * Detect the active `/command` query for the text up to the caret.
 *
 * Returns the characters typed after the leading `/` (may be empty, right after
 * typing `/`), or `null` when the text does not start with `/` or the command
 * token has already ended at a space.
 */
export function detectSlashQuery(textUpToCaret: string): string | null {
  if (!textUpToCaret.startsWith("/")) return null;
  const rest = textUpToCaret.slice(1);
  // A space ends the command token — the menu closes and any trailing text is
  // treated as free-text args (or the user must pick a command first).
  if (/\s/.test(rest)) return null;
  return rest;
}

/** Filter the catalogue by a slash query, matching id / label / hint. */
export function filterSlashCommands(
  commands: KaiSlashCommand[],
  query: string,
): KaiSlashCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter(
    (c) =>
      c.id.toLowerCase().includes(q) ||
      c.label.toLowerCase().includes(q) ||
      c.hint.toLowerCase().includes(q),
  );
}
